<!--
Copyright (c) 2020 GanttProject Cloud OU.
Author: Dmitry Barashev (dbarashev@ganttproject.cloud)
-->
<template>
<div class="col-md-6 pl-md-4">
  <div class="d-flex flex-wrap">
    <h4 class="flex-fill">Billing Accounts</h4>
    <div class="flex-wrap d-flex align-items-baseline">
      <button type="button" role="button" data-toggle="modal"
              data-target="#modal-new-billing"
              class="btn btn-attention btn-new-item pull-right w-100">
        New Account
      </button>
    </div>
  </div>

  <div class="pt-3">
    <div class="tab-content">
      <div class="tab-pane fade show active"
           id="billing-accounts" role="tabpanel" aria-labelledby="tab-team-own">
          <div class="list-group">
            <a href="javascript:void(0)"
               v-for="acc in accounts"
               class="list-group-item list-group-item-action flex-column align-items-start"
               :data-refid="acc.refid" @click="select(acc)">
              <div class="row">
                <div class="col-sm-4 col-lg-3 d-flex flex-sm-column align-items-center justify-content-center">
                  <i class="fas fa-5x fa-coins d-inline-block d-sm-block m-2"></i>
                  <b class="d-inline-block d-sm-block m-2">Credit: {{acc.credit}}</b>
                </div>
                <div class="col-sm-8 col-lg-9 d-flex flex-column">
                  <div class="d-flex">
                    <h5 class="flex-fill">{{acc.name}}</h5>
                    <div class="ml-1">
                      <button class="btn btn-outline-secondary btn-icon btn-menu"
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false">
                        <i class="fas fa-ellipsis-v"></i>
                      </button>
                      <div class="dropdown-menu">
                        <a class="dropdown-item" href="#" @click="editBilling(acc)">
                          <i class="fa fa-lock fa-fw"></i>&nbsp;Edit&hellip;
                        </a>
                      </div>
                    </div>
                  </div>
                  <div class="help-text">
                    {{acc.description}}
                  </div>
                  <div class="flex-fill"></div>
                  <div class="d-flex justify-content-end align-items-center" v-if="isEnabled">
                    <button class="btn btn-neutral-outline btn-sm mr-2" @click="onPromoClick(acc)">Promo Code</button>
                    <button class="btn btn-attention-outline btn-sm" @click="onTopupClick(acc)">Top Up Credit</button>
                  </div>
                </div>
              </div>
              <div class="row" v-if="promoAccountRefid === acc.refid && isPromoSuccess">
                <div class="col-12 mt-3">
                  <div class="alert alert-success alert-dismissible fade show" role="alert">
                    <p>{{promoSuccessMessage}}</p>
                    <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="isPromoSuccess = false">
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="row" v-if="isPaymentSuccess">
                <div class="col-12 mt-3">
                  <div class="alert alert-success alert-dismissible fade show" role="alert">
                    <p>Your payment was successful, and the purchased credit has been added to your account.</p>
                    <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="row" v-if="isPaymentError">
                <div class="col-12 mt-3">
                  <div class="alert alert-danger alert-dismissible fade show" role="alert">
                    <p>{{paymentErrorMessage}}</p>
                    <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                    <p>Please <a target="_blank" href="/about/contacts">contact us</a> to resolve this issue. Thanks for your patience!</p>
                  </div>
                </div>
              </div>
            </a>
          </div>
      </div>
    </div>
  </div>

  <BillingNewModal ref="modal" :dashboard-refresh="loadBillingAccounts"></BillingNewModal>
  <PromoCodeModal ref="promoModal" :on-success="onPromoSuccess"></PromoCodeModal>
</div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-facing-decorator';
import {BillingDto} from '@/Teams';
import {AboutBeta, MeBillingList, Signin} from '@/Routes';
import {handleXhrError, showError, SelectionModel} from '@/utils/Ui';
import * as Auth from '../auth/Auth';
import {User} from '../auth/Auth';
import BillingNewModal from '@/components/BillingNewModal.vue';
import BillingAccountHistory from "@/components/BillingAccountHistory.vue";
import PromoCodeModal from '@/components/PromoCodeModal.vue';
import {PaymentPaddle} from '@/components/PaymentPaddle';

@Component({
  components: {BillingNewModal, PromoCodeModal}
})
export default class BillingPane extends Vue {

  public accounts: List<BillingDto> = [];

  public readonly routes = {
  };
  private isEnabled = true;
  private isPaymentError = false;
  private isPaymentSuccess = false;
  private paymentErrorMessage = '';
  private promoAccountRefid = '';
  private isPromoSuccess = false;
  private promoSuccessMessage = '';
  private selectionModel = new SelectionModel<BillingDto>(
      "#billing-accounts",
      ".list-group-item",
      "data-refid",
      (a: BillingDto, b: BillingDto) => a.refid === b.refid
  );
  private selectedAccount: BillingDto;
  private historyPane: BillingAccountHistory;
  private paymentPaddle = new PaymentPaddle();

  public $refs: {
    modal: BillingNewModal,
    promoModal: PromoCodeModal
  }

  public mounted() {
    this.paymentPaddle.init();
  }

  public setHistoryPane(pane: BillingAccountHistory) {
    this.historyPane = pane;
    if (this.selectedAccount) {
      this.historyPane.setSelectedAccount(this.selectedAccount);
    }
  }

  public setUser(user: User) {
    this.isEnabled = true;
    this.loadBillingAccounts();
  }

  private loadBillingAccounts() {
    MeBillingList.invoke(undefined).then((list: List<BillingDto>) => {
      this.accounts = list;
    }).then(() => {
      const id2model = (billingId) => this.accounts.find((dto) => dto.refid === billingId);
      this.selectionModel.init(id2model, (billing) => {
        this.select(billing);
      }).resolve();
    }).fail((jqXhr) => handleXhrError(jqXhr, 'Failed to load billing accounts'));
  }

  private onTopupClick = this.openPaddlePaymentOverlay;

  private openPaddlePaymentOverlay(acc: BillingDto) {
    Auth.init().then((user: User) => {
      this.paymentPaddle.errorHandler = this.showPaymentError;
      this.paymentPaddle.successHandler = () => {
        this.showPaymentSuccess();
        this.select(acc);
      }
      this.paymentPaddle.openCheckout(acc.refid);
    }).catch(() => Signin.redirect());
  }
  private showPaymentError(error: string) {
    this.paymentErrorMessage = error;
    this.isPaymentError = true;
  }
  private showPaymentSuccess() {
    this.isPaymentSuccess = true
  }
  private onPromoClick(acc: BillingDto) {
    this.promoAccountRefid = acc.refid;
    this.isPromoSuccess = false;
    this.$refs.promoModal.applyPromoCode(acc.refid);
  }

  private onPromoSuccess(credit: number) {
    this.promoSuccessMessage = `Promo code applied: ${credit} credits have been added to your account.`;
    this.isPromoSuccess = true;
    this.loadBillingAccounts();
  }
  private editBilling(acc: BillingDto) {
    this.$refs.modal.editBilling(acc);
  }

  private select(acc: BillingDto) {
    this.historyPane.setSelectedAccount(acc);
    this.selectedAccount = acc;
  }
}
</script>

<style lang="scss" scoped>
@import "sass/components/variables";

h4 {
  font-family: 'Saira Condensed', sans-serif;
}
.list-group-item.active .btn-attention-outline {
  color: $gp-gray-on-dark;
  &:hover {
    color: $gp-dark-brand;
  }
}
.btn-neutral-outline {
  background-color: transparent;
  border-color: $gp-gray-on-dark;
  color: $gp-gray-on-dark;
  font-family: 'Saira Condensed', sans-serif;
  &:hover, &:focus:hover {
    border-color: $gp-medium-brand;
    color: $gp-dark-brand;
    background-color: $gp-gray-on-dark;
  }
}
</style>
